import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import type { CalculationResult } from "@/types/calculator";
import { useThemeStore } from "@/lib/themeStore";
import { SLICE_COLORS, buildVramSummary, donutArcPath, fmtGb, type SliceKey } from "@/lib/vramBreakdown";

const SIZE = 220;
const R_OUTER = 100;
const R_INNER = 62;
const POP = 6; // hovered slice grows outward

// Donut of installed VRAM split into what occupies it; the table beside it
// explains each slice and doubles as the accessible (non-colour) view.
export function VramBreakdownChart({ result }: { result: CalculationResult }) {
  const { dark } = useThemeStore();
  const [hovered, setHovered] = useState<SliceKey | null>(null);
  const s = buildVramSummary(result);
  const color = (k: SliceKey) => SLICE_COLORS[k][dark ? "dark" : "light"];
  const surface = dark ? "#1f2937" : "#ffffff"; // card background: the 2px gap between slices
  const hoveredSlice = s.slices.find((x) => x.key === hovered);
  const usedPct = s.totalGb > 0 ? Math.round((s.usedGb / s.totalGb) * 100) : 0;
  const free = s.slices.find((x) => x.key === "free");

  // Slices are drawn over the bar length: installed VRAM, or the requirement
  // when the workload doesn't fit (then there is no free slice).
  let angle = 0;
  const arcs = s.slices.map((slice) => {
    const sweep = (slice.gb / s.scaleGb) * Math.PI * 2;
    const a = { slice, start: angle, end: angle + sweep };
    angle += sweep;
    return a;
  });
  const c = SIZE / 2;

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 mb-1">
        <h4 className="font-semibold text-gray-900 dark:text-white">GPU memory breakdown</h4>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {s.gpuCount} × {s.vramPerGpu} GB GPUs = {fmtGb(s.totalGb)} installed
        </span>
      </div>
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
        {s.shortfallGb > 0 ? (
          <>
            The workload needs <span className="font-semibold text-gray-900 dark:text-white">{fmtGb(s.usedGb)}</span>,
            but only {fmtGb(s.totalGb)} is installed
          </>
        ) : (
          <>
            The workload uses <span className="font-semibold text-gray-900 dark:text-white">{fmtGb(s.usedGb)}</span> of
            the {fmtGb(s.totalGb)} installed ({usedPct}%)
            {free ? (
              <> · <span className="font-semibold text-gray-900 dark:text-white">{fmtGb(free.gb)}</span> free headroom</>
            ) : null}
          </>
        )}
      </p>

      {s.shortfallGb > 0 && (
        <div className="flex items-start gap-2 mb-3 text-sm text-red-800 dark:text-red-200">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-[#d03b3b]" aria-hidden />
          <span>
            Short by <strong>{fmtGb(s.shortfallGb)}</strong>: the workload plus reserved overhead exceeds the
            installed VRAM, so the chart shows the full requirement.
          </span>
        </div>
      )}

      {/* Tooltip line: always reserves its height so hovering doesn't shift layout. */}
      <div className="min-h-5 mb-2 text-xs text-gray-700 dark:text-gray-300" aria-live="polite">
        {hoveredSlice ? (
          <span>
            <span className="font-semibold text-gray-900 dark:text-white">{hoveredSlice.label}</span>
            {" · "}
            {fmtGb(hoveredSlice.gb)} ({hoveredSlice.pct.toFixed(1)}%) · {hoveredSlice.detail}
          </span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">Hover a slice for details</span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 items-center">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="w-[220px] h-[220px] mx-auto overflow-visible"
          role="img"
          aria-label={`GPU memory: ${s.slices.map((x) => `${x.label} ${fmtGb(x.gb)}`).join(", ")}`}
        >
          {arcs.map(({ slice, start, end }) => (
            <path
              key={slice.key}
              d={donutArcPath(c, c, R_INNER, hovered === slice.key ? R_OUTER + POP : R_OUTER, start, end)}
              fill={color(slice.key)}
              stroke={surface}
              strokeWidth={2}
              strokeLinejoin="round"
              opacity={hovered && hovered !== slice.key ? 0.45 : 1}
              className="transition-opacity cursor-pointer"
              onMouseEnter={() => setHovered(slice.key)}
              onMouseLeave={() => setHovered(null)}
            />
          ))}
          <text x={c} y={c - 4} textAnchor="middle" className="fill-gray-900 dark:fill-white" style={{ fontSize: 26, fontWeight: 700 }}>
            {usedPct}%
          </text>
          <text x={c} y={c + 16} textAnchor="middle" className="fill-gray-500 dark:fill-gray-400" style={{ fontSize: 11 }}>
            {s.shortfallGb > 0 ? "of installed" : "in use"}
          </text>
        </svg>

        {/* Legend + table view */}
        <table className="w-full text-sm">
          <thead className="sr-only">
            <tr><th>Component and how it is sized</th><th>GB</th><th>Share of installed VRAM</th></tr>
          </thead>
          <tbody>
            {s.slices.map((slice) => (
              <tr
                key={slice.key}
                className={`border-t border-gray-100 dark:border-gray-700 first:border-t-0 ${hovered === slice.key ? "bg-gray-50 dark:bg-gray-700/40" : ""}`}
                onMouseEnter={() => setHovered(slice.key)}
                onMouseLeave={() => setHovered(null)}
              >
                <td className="py-2 pr-3 align-top">
                  <div className="flex items-start gap-2">
                    <span className="mt-1 inline-block w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: color(slice.key) }} />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{slice.label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{slice.detail}</div>
                    </div>
                  </div>
                </td>
                <td className="py-2 pl-3 text-right align-top font-semibold text-gray-900 dark:text-gray-100 tabular-nums whitespace-nowrap">
                  {fmtGb(slice.gb)}
                </td>
                <td className="py-2 pl-3 text-right align-top text-xs text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap w-14">
                  {slice.pct.toFixed(1)}%
                </td>
              </tr>
            ))}
            <tr className="border-t border-gray-200 dark:border-gray-600">
              <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">Installed VRAM</td>
              <td className="py-2 pl-3 text-right font-semibold text-gray-900 dark:text-gray-100 tabular-nums whitespace-nowrap">{fmtGb(s.totalGb)}</td>
              <td className="py-2 pl-3 text-right text-xs text-gray-500 dark:text-gray-400 tabular-nums">100%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
